import React, {createContext, useCallback, useContext, useEffect} from 'react';
import PropTypes from "prop-types";

import MobileStepper from "@mui/material/MobileStepper";
import Slide from "@mui/material/Slide";
import useMediaQuery from "@mui/material/useMediaQuery";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import LoadingButton from "@mui/lab/LoadingButton";
import Stack from "@mui/material/Stack";

import useTheme from "@mui/material/styles/useTheme";

import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';


const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});


// A context that keep track of the state of the stepper
const InternalStepperContext = createContext();
export const useStepperContext = () => useContext(InternalStepperContext);

export const StepperContext = ({children}) => {
    const [state, updateState] = React.useReducer(
        (state, action) => {
            switch (action.type) {
                case "next":
                    return {...state, activeStep: state.activeStep + 1}
                case "back":
                    return {...state, activeStep: state.activeStep - 1}
                case "setActiveStep":
                    return {...state, activeStep: action.step}
                case "setNumberOfSteps":
                    return {...state, numberOfSteps: action.numberOfSteps}
                default:
                    throw new Error(`Unknown action ${action}`);
            }
        },
        {
            numberOfSteps: 0,
            activeStep: 0
        }
    );

    const isFirstStep = state.activeStep === 0;
    const isLastStep = state.activeStep === state.numberOfSteps - 1;

    const back = useCallback(
        () => updateState({type: "back"})
        , []
    );

    const next = useCallback(
        () => updateState({type: "next"}),
        []
    );

    const setActiveStep = useCallback(
        (step) => updateState({type: "setActiveStep", step: step}),
        []
    );

    const setNumberOfSteps = useCallback(
        (val) => updateState({type: "setNumberOfSteps", numberOfSteps: val}),
        []
    );

    const context = {
        activeStep: state.activeStep,
        isFirstStep,
        isLastStep,
        back,
        next,
        setActiveStep,
        setNumberOfSteps,
    }

    return <InternalStepperContext.Provider value={context}>
        {children}
    </InternalStepperContext.Provider>
}

const ResponsiveStepper = props => {
    const {
        title,
        isOpen,
        isSaving,
        onClose,
        onNext,
        onBack,
        onFinish = f => f,
        stepLabels,
        disableNext,
        disableBackdropClose,
        saveLabel = "Salva",
        commonHeader,
        children
    } = props;

    const context = useStepperContext();

    if (context === undefined) {
        throw new Error("ResponsiveStepper should be inside of a StepperContext")
    }

    const {activeStep, isFirstStep, isLastStep, next, back, setNumberOfSteps} = context
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const steps = React.Children.toArray(children);

    const nextLabel = "Avanti";
    const backLabel = "Indietro";
    const cancelLabel = "Cancella";

    // Set the handleBack and handleNext to the default function or to the given user functions
    const handleBack = !!onBack ? onBack : back;
    const handleNext = !!onNext ? onNext : next;

    const handleClose = (object, reason) => {
        if (disableBackdropClose && reason === "backdropClick") {
            return;
        }
        if (isSaving) {
            return;
        }
        onClose();
    };

    const getStepContent = () => {
        if (activeStep >= 0 && activeStep < steps.length) {
            return steps[activeStep];
        } else {
            throw new Error('Unknown step');
        }
    };

    useEffect(
        () => {
            setNumberOfSteps(steps.length)
        }, [steps.length, setNumberOfSteps]
    )

    return <Dialog
        fullScreen={isMobile}
        fullWidth={true}
        maxWidth="md"
        TransitionComponent={Transition}
        open={isOpen}
        onClose={handleClose}
        aria-labelledby={title}>

        <Stack style={{width: "100%", height: "100%"}}>
            {
                // TITLE
                isMobile ? (
                    <AppBar position="relative">
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                                <CloseIcon/>
                            </IconButton>
                            <Typography variant="h6" sx={{ml: 2, flexGrow: 1}}>
                                {title}
                            </Typography>
                            <LoadingButton autoFocus
                                           color="inherit"
                                           type="button"
                                           onClick={onFinish}
                                           disabled={!isLastStep || !!disableNext}
                                           loading={isSaving}>
                                {saveLabel}
                            </LoadingButton>
                        </Toolbar>
                    </AppBar>
                ) : (
                    <React.Fragment>
                        <DialogTitle>{title}</DialogTitle>
                    </React.Fragment>
                )
            }

            <DialogContent>
                {
                    !isMobile &&
                    <Stepper activeStep={activeStep} sx={{pt: 1, pb: 3}}>
                        {stepLabels.map((label, i) => (
                            <Step key={i}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                }
                {!!commonHeader && commonHeader}
                {getStepContent()}
            </DialogContent>

            {
                isMobile ?
                    <MobileStepper
                        variant="dots"
                        steps={steps.length}
                        position="static"
                        activeStep={activeStep}
                        nextButton={
                            <LoadingButton size="small"
                                           type="button"
                                           onClick={isLastStep ? onFinish : handleNext}
                                           disabled={disableNext}
                                           loading={isSaving}>
                                {isLastStep ? saveLabel : <>{nextLabel}<KeyboardArrowRight/></>}
                            </LoadingButton>
                        }
                        backButton={
                            <Button size="small"
                                    type="button"
                                    onClick={handleBack}
                                    disabled={isFirstStep}>
                                <KeyboardArrowLeft/>
                                {backLabel}
                            </Button>
                        }
                    />
                    :
                    <DialogActions sx={{py: 2, px: 3}}>
                        <Button sx={{marginRight: "auto"}}
                                type="button"
                                onClick={handleClose}>
                            {cancelLabel}
                        </Button>
                        {
                            !isFirstStep &&
                            <Button type="button"
                                    onClick={handleBack}>
                                {backLabel}
                            </Button>
                        }
                        <LoadingButton variant="contained"
                                       type="button"
                                       onClick={isLastStep ? onFinish : handleNext}
                                       disabled={!!disableNext}
                                       loading={isSaving}>
                            {isLastStep ? saveLabel : nextLabel}
                        </LoadingButton>
                    </DialogActions>
            }
        </Stack>
    </Dialog>
};

ResponsiveStepper.propTypes = {
    title: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    isSaving: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onNext: PropTypes.func,
    onBack: PropTypes.func,
    onFinish: PropTypes.func,
    stepLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
    disableBackdropClose: PropTypes.bool,
    disableNext: PropTypes.bool,
    commonHeader: PropTypes.element,
};

export {ResponsiveStepper}